import { $fetch } from '@/boot/fetch'
import { dateRangeObjectToApiFormat } from '@/utils/dates.util.js'

function endpoint(retailerId) {
  return `/retailers/${retailerId}/agents/reporting`
}

export default {
  listSnapshots(retailerId, params = {}) {
    const query = new URLSearchParams()
    if (params.sortBy) query.set('sortBy', params.sortBy)
    if (params.dateRange) {
      const dr =
        typeof params.dateRange === 'object' && params.dateRange.from
          ? dateRangeObjectToApiFormat(params.dateRange)
          : params.dateRange
      query.set('dateRange', dr)
    }
    if (params.limit != null) query.set('limit', String(params.limit))
    if (params.offset != null) query.set('offset', String(params.offset))
    const qs = query.toString()
    return $fetch.peg(`/retailers/${retailerId}/reports/snapshots${qs ? `?${qs}` : ''}`)
  },
  getSnapshot(retailerId, snapshotId) {
    return $fetch.peg(`/retailers/${retailerId}/reports/snapshots/${snapshotId}`)
  },
  startChat(retailerId, message) {
    return $fetch.peg(`${endpoint(retailerId)}/start`, {
      method: 'POST',
      body: message ? { message } : {},
    })
  },
  sendMessage(retailerId, conversationId, message) {
    return $fetch.peg(`${endpoint(retailerId)}/message`, {
      method: 'POST',
      body: { conversationId, message },
    })
  },
  /**
   * PEG-914 (parent PEG-538): list the retailer's reporting conversations,
   * sorted most-recently-updated first, excluding soft-deleted ones.
   * Paginated — limit (default 20, max 100) + offset. Resolves to
   *   { conversations: AIConversation[], pagination: { limit, offset, total, hasMore } }
   * Conversations are retailer-scoped server-side (no client-side filtering).
   * @param {string|number} retailerId
   * @param {{ limit?: number, offset?: number }} [params]
   */
  listConversations(retailerId, params = {}) {
    const query = new URLSearchParams()
    if (params.limit != null) query.set('limit', String(params.limit))
    if (params.offset != null) query.set('offset', String(params.offset))
    const qs = query.toString()
    return $fetch.peg(`${endpoint(retailerId)}/conversations${qs ? `?${qs}` : ''}`)
  },
  /**
   * PEG-918 (AC2/AC3): eager-create an empty conversation. New Chat and the
   * first-visit auto-create call this so a real conversation row appears at the
   * top of the list immediately, before any message is sent. Runs no agent turn
   * and consumes no monthly quota server-side (message_count=0, status active,
   * title null). The body is empty — conversationId is server-generated and
   * returned on the row. The endpoint is idempotent: it reuses the retailer's
   * most recent empty active conversation when one exists. Resolves to the new
   * (or reused) conversation row:
   *   { conversationId, title, messageCount, messageLimit, status,
   *     dateCreated, dateModified }
   * The first user message is then sent via sendMessage() against the returned
   * conversationId (NOT startChat) — see useReportingChat.sendMessage.
   * @param {string|number} retailerId
   */
  createConversation(retailerId) {
    return $fetch.peg(`${endpoint(retailerId)}/conversations`, {
      method: 'POST',
      body: {},
    })
  },
  /**
   * PEG-916: Fetch one conversation's full turn history (AC6). Returns the
   * conversation metadata plus the chronological message list:
   *   { conversation: { conversationId, title, messageCount, messageLimit,
   *                     status, perConversationRemaining },
   *     messages: [ { role, content, blocks, timestamp }, ... ] }
   * Retailer-scoped — a foreign or soft-deleted conversation reads as 404.
   */
  getConversationMessages(retailerId, conversationId) {
    // PEG-916: conversationId can originate from the URL query (AC8 refresh
    // persistence), so encode it before interpolating into the path — an id
    // containing '/' or '?' would otherwise break the path or risk injection.
    return $fetch.peg(`${endpoint(retailerId)}/conversations/${encodeURIComponent(conversationId)}/messages`)
  },
  /**
   * PEG-914: soft delete a conversation (hidden from list, data retained).
   * Retailer-scoped + idempotent — a foreign/already-deleted/unknown id 404s.
   * @param {string|number} retailerId
   * @param {string} conversationId
   */
  deleteConversation(retailerId, conversationId) {
    // PEG-914: conversationId can originate from the URL query (AC8), so encode
    // it before interpolating into the path — matches getConversationMessages
    // above; an id containing '/' or '?' would otherwise break the path.
    return $fetch.peg(`${endpoint(retailerId)}/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
    })
  },
  /**
   * PEG-670: Fetch current monthly AI quota status for a retailer.
   * Returns QuotaStatus shape:
   *   { monthlyLimit, monthlyUsed, monthlyRemaining, resetsAt, isExhausted, overrideActive }
   * Endpoint is mounted at /retailers/:retailerId/ai/quota (PEG-669), NOT under
   * /agents/reporting — keep this path in sync with the backend mount.
   */
  getQuota(retailerId) {
    return $fetch.peg(`/retailers/${retailerId}/ai/quota`)
  },
}
